import {NetworkTypesEnum} from 'domain/usecases';
import {FlowEnum} from 'presentation/pages/private/booking/constants/flowEnum';
import {TelemedicineEnum} from 'presentation/pages/private/booking/constants/telemedicineEnum';
import {useMediaQueries, useQueryParams} from 'presentation/pages/private/hooks';
import {HandleSelectCardParams, UseUnitListProps} from './types';

import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {useUnitsListStore} from 'presentation/modules/booking/shared/store/useUnitsListStore';

import {AppointmentBookingEnum} from 'main/analytics/gaTagsEnum';
import {FeaturesEnum, TelehealthBookingEnum} from 'presentation/constants';
import {useHostName} from 'presentation/hooks';
import {useReverseNetworkOrder} from 'presentation/hooks/useReverseNetworkOrder';
import {MIN_DISTANCE_IN_KM_HEALTH} from 'presentation/modules/booking/health/ui/components/HealthSearchBar/hooks/useHealthSearchBar/constants';
import {BookingTypeEnum} from 'presentation/modules/booking/shared/constants/bookingTypeEnum';
import {TELEMEDICINE_INFO} from 'presentation/modules/booking/shared/constants/telemedicineInfo';
import {MAX_DISTANCE_FILTER_UNITS} from 'presentation/modules/booking/shared/constants/unitsInfo';
import {useBookingSummary} from 'presentation/modules/booking/shared/hooks/useBookingSummary';
import {useBookingFlowStepsStore} from 'presentation/modules/booking/shared/store/useBookingFlowStepsStore';
import {useOpenDrawerFilterStore} from 'presentation/modules/booking/shared/store/useOpenDrawerFilterStore';
import {Units} from 'presentation/modules/booking/shared/store/useUnitsListStore/types';
import {ChangeFlowVariantEnum} from 'presentation/modules/booking/shared/ui/components/ChangeFlowDialog/constants';
import {getUnitsByDistanceTitle} from 'presentation/modules/booking/shared/utils/getUnitsByDistanceTitle';
import {getTelehealthStepPosition} from 'presentation/modules/booking/telehealth/utils/getTelehealthStepPosition';
import {translations} from 'presentation/translations-new';
import {useNavigate} from 'react-router-dom';
import {useHealthNetworkVisibility} from '../../../../hooks/useHealthNetworkVisibility';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';

const {changeDistance, searchNearbyUnitsHealthExam, changeAppointmentLocal, selfNetwork} =
	translations['pt-br'].privateFlow.bookingFlow.unitsFlow;

export function useHealthUnitsSelfNetwork({list, handleNextStep}: UseUnitListProps) {
	const bookingData = useBookingStore(state => state.bookingData);
	const setBookingData = useBookingStore(state => state.setBookingData);
	const setActiveBookingFlowStep = useBookingFlowStepsStore(state => state.setBookingStep);
	const navigate = useNavigate();
	const {isMobile} = useMediaQueries();
	const {isGndiSaoPaulo} = useHostName();
	const {handleChangeModals} = useBookingSummary();
	const setOpenDrawerState = useOpenDrawerFilterStore(state => state.setOpenDrawerState);
	const {
		SEARCH_PARAMS: {query, localityDistance},
		clearSearch,
	} = useQueryParams();
	const {isEnabled: isUnitsNetworksEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_BOOKING_UNITS_NETWORKS,
	);

	const {shouldReverseNetworkOrder} = useReverseNetworkOrder({flowType: 'HEALTH'});
	const {showAccreditedNetworkToHap} = useHealthNetworkVisibility();

	const {specialtyName, isManualLocation, isFavoriteDoctorsFlow} = bookingData;

	const unitsListState = useUnitsListStore(state => state.unitsList);

	const hasAnyUnit = Boolean(
		unitsListState?.units?.unitsByLocality?.length ||
		unitsListState?.units?.unitsByDistance?.length ||
		unitsListState?.units?.unitsSupportAccredited?.length,
	);

	const listWithoutTelemedicine = {
		...list,
		units: {
			...list.units,
			unitsByLocality: list.units.unitsByLocality.filter(
				item =>
					(item as Units<FlowEnum.HEALTH>)?.telemedicine !==
					TelemedicineEnum.IS_TELEMEDICINE,
			),
		},
	};

	const isSelfNetworkType = listWithoutTelemedicine.networkType.name === NetworkTypesEnum.SELF;

	const activeFilters = {
		distance: Boolean(localityDistance),
	};

	const filtersState = {
		isDistanceAtMax: Number(localityDistance) >= MAX_DISTANCE_FILTER_UNITS,
		isAnyFilterActive: activeFilters.distance,
	} as const;

	const {unitsByLocality, unitsByDistance, unitsSupportAccredited} =
		listWithoutTelemedicine?.units || {};

	const isEmpty = {
		locality: unitsByLocality?.length === 0,
		distance: unitsByDistance?.length === 0,
		accredited: unitsSupportAccredited?.length === 0,
	};

	const unitsByDistanceTitle = getUnitsByDistanceTitle(
		unitsByDistance?.length ?? 0,
		localityDistance,
	);

	const showEmptyAlert = (isEmpty.locality && isEmpty.distance) || !isSelfNetworkType;

	const shouldHideDivider =
		isGndiSaoPaulo || showAccreditedNetworkToHap ? isEmpty.accredited && Boolean(query) : true;

	const shouldChangeLocation =
		filtersState.isDistanceAtMax && isEmpty.locality && isEmpty.distance && !isEmpty.accredited;

	const shouldHideDistanceButton =
		isUnitsNetworksEnabled &&
		showEmptyAlert &&
		filtersState.isDistanceAtMax &&
		!shouldChangeLocation;

	const shouldHideSelfNetworkButton = Boolean(isFavoriteDoctorsFlow) || shouldHideDistanceButton;

	const showUnitsByDistanceTitle = () => {
		if (isManualLocation && !isEmpty.distance) return true;

		return activeFilters.distance && localityDistance !== MIN_DISTANCE_IN_KM_HEALTH;
	};

	const openDrawerButtonLabel = () => {
		if (filtersState.isAnyFilterActive) {
			return changeDistance;
		}

		return searchNearbyUnitsHealthExam;
	};

	const getButtonLabel = () => {
		if (shouldChangeLocation) {
			return changeAppointmentLocal;
		}

		return openDrawerButtonLabel();
	};

	const handleChangeLocation = () => handleChangeModals(ChangeFlowVariantEnum.LOCATION);

	const handleOpenDrawer = () => {
		setOpenDrawerState(true);
	};

	const getButtonAction = () => {
		if (shouldChangeLocation) {
			return handleChangeLocation();
		}

		return handleOpenDrawer();
	};

	const getButtonDataTagId = () => {
		if (shouldChangeLocation) {
			return AppointmentBookingEnum.CLICK_ALTERAR_LOCAL_ATENDIMENTO_AGENDAMENTO;
		}

		return filtersState.isAnyFilterActive
			? AppointmentBookingEnum.ALTEROU_DISTANCIA
			: AppointmentBookingEnum.PROCUROU_UNIDADES_PROXIMAS;
	};

	const handleSelectCard = (params: HandleSelectCardParams) => {
		if (params.isTelemedicine) {
			setBookingData(prev => ({
				...prev,
				unitCode: params.code,
				unitName: TELEMEDICINE_INFO.name,
				unitAddress: '',
				ufCode: TELEMEDICINE_INFO.ufCode,
				ufName: TELEMEDICINE_INFO.ufName,
				cityName: TELEMEDICINE_INFO.cityName,
				bookingRedirect: true,
				bookingType: BookingTypeEnum.TELEHEALTH,
			}));

			const {PROFESSIONAL_STEP} = getTelehealthStepPosition();

			setActiveBookingFlowStep(PROFESSIONAL_STEP);
			navigate(TelehealthBookingEnum.TELEHEALTH_BOOKING_FLOW);
			clearSearch();
			return;
		}

		setBookingData(prev => ({
			...prev,
			unitCode: params.code,
			unitName: params.name,
			unitAddress: params.address,
			bookingRedirect: false,
		}));

		if (isFavoriteDoctorsFlow) {
			setBookingData(prev => ({
				...prev,
				ufCode: params?.ufCode || prev.ufCode,
				ufName: params?.ufName || prev.ufName,
				cityName: params?.cityName || prev.cityName,
			}));
		}

		clearSearch();
		handleNextStep();
	};

	const shouldHideSelfNetworkComponent = () => {
		if (!query || !hasAnyUnit) return false;
		if (!isSelfNetworkType) return true;
		return isEmpty.locality && isEmpty.distance;
	};

	const getEmptyAlertDescription = () => {
		if (activeFilters.distance && !(isUnitsNetworksEnabled && filtersState.isDistanceAtMax)) {
			return `${selfNetwork.emptyDescriptionInKmPart1}${localityDistance}${selfNetwork.emptyDescriptionInKmPart2}`;
		}

		return selfNetwork.emptyDescription;
	};

	return {
		handleSelectCard,
		query,
		isMobile,
		specialtyName,
		unitsByDistance,
		unitsByDistanceTitle,
		listByDistanceLength: unitsByDistance?.length ?? 0,
		showEmptyAlert,
		shouldHideSelfNetworkComponent,
		shouldHideDivider,
		shouldHideSelfNetworkButton,
		getButtonAction,
		getButtonLabel,
		getButtonDataTagId,
		getEmptyAlertDescription,
		showUnitsByDistanceTitle,
		shouldReverseNetworkOrder,
		unitsByLocality,
		isUnitsNetworksEnabled,
	};
}
